
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useCandidateDetail } from "@/hooks/candidate/useCandidateDetail";
import { EditCandidateModal } from "@/components/candidates/EditCandidateModal";
import { Candidate } from "@/types/candidate";
import { useSettings } from "@/providers/SettingsProvider";
import { toast } from "@/hooks/use-toast";
import { CandidateDetailHeader } from "@/components/candidates/CandidateDetailHeader";
import { CandidateProfile } from "@/components/candidates/CandidateProfile";
import { CandidateTabs } from "@/components/candidates/CandidateTabs";
import { CandidateEmbeddingWrapper } from "@/components/candidates/CandidateEmbeddingWrapper";

const CandidateDetail = () => {
  
  const { id } = useParams<{ id: string }>();
  
  const { 
    loading, 
    candidate, 
    cv, 
    cvExists,
    comments, 
    handleDelete, 
    handleAddComment,
    handleUpdateComment,
    handleDeleteComment,
    handleUpdateCandidate,
    refetchCandidate
  } = useCandidateDetail(id);
  
  const { devMode } = useSettings();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentCV, setCurrentCV] = useState<string | null>(null);
  const [currentCVExists, setCurrentCVExists] = useState(cvExists);
  const [activeTab, setActiveTab] = useState("processes");
  
  // Update CV state when the data changes
  useEffect(() => {
    if (cv !== currentCV) {
      setCurrentCV(cv);
    }
    
    if (cvExists !== currentCVExists) {
      setCurrentCVExists(cvExists);
    }
  }, [cv, cvExists]);
  
  const handleEditSuccess = async (updatedData: Partial<Candidate>) => {
    try {
      console.log("Handling edit success with data:", updatedData);
      await handleUpdateCandidate(updatedData);
      
      // Explicitly refetch data after successful update
      if (refetchCandidate) {
        console.log("Refetching candidate data after update");
        await refetchCandidate();
      }
      
      setIsEditModalOpen(false);
      
      toast({
        title: "Success",
        description: "Candidate updated successfully",
      });
    } catch (error) {
      console.error("Failed to update candidate:", error);
      toast({
        title: "Error",
        description: "Failed to update candidate",
        variant: "destructive",
      });
    }
  };

  const handleCVUpdate = (newCVUrl: string) => {
    setCurrentCV(newCVUrl);
    setCurrentCVExists(true);
  };
  
  const handleLocalDelete = async () => {
    if (id) {
      try {
        await handleDelete();
      } catch (error) {
        console.error("Error during delete process:", error);
        toast({
          title: "Error",
          description: "Failed to delete candidate",
          variant: "destructive",
        });
      }
    }
  };
  
  if (loading) {
    return (
      <div className="animate-pulse p-6">
        <div className="h-12 bg-gray-200 rounded mb-6"></div>
        <div className="space-y-4">
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }
  
  if (!candidate) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <h2 className="text-xl font-semibold mb-2">Candidate Not Found</h2>
          <p className="text-gray-600">The candidate you are looking for does not exist or has been deleted.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full">
      <CandidateDetailHeader 
        isEditModalOpen={isEditModalOpen}
        setIsEditModalOpen={setIsEditModalOpen}
        handleDelete={handleLocalDelete}
      />
      
      <CandidateProfile 
        candidate={candidate}
        cv={currentCV}
        cvExists={currentCVExists}
        onCVUpdate={handleCVUpdate}
      />
      
      <CandidateEmbeddingWrapper 
        candidate={candidate}
        devMode={devMode}
        onUpdate={() => refetchCandidate()}
      />
      
      <CandidateTabs 
        candidateId={candidate.id}
        comments={comments}
        cv={currentCV}
        candidate={candidate}
        handleAddComment={handleAddComment}
        handleUpdateComment={handleUpdateComment}
        handleDeleteComment={handleDeleteComment}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {candidate && (
        <EditCandidateModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleEditSuccess}
          candidate={candidate}
        />
      )}
    </div>
  );
};

export default CandidateDetail;
